import { useState, useRef, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Grid,
} from "@mui/material"
import {
  Close as CloseIcon,
  Print as PrintIcon,
  FileDownload as DownloadIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material"
import { useReactToPrint } from "react-to-print"
import * as XLSX from "xlsx"
import { useApiQuery } from "../../hooks/useApi"
import { useQueryClient } from "@tanstack/react-query"

function ReportGenerator({ open, onClose }) {
  const [filterType, setFilterType] = useState("month")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const reportRef = useRef()

  // Compute URL dynamically
  const reportUrl = useMemo(() => {
    if (filterType === "custom" && startDate && endDate) {
      return `/users/report?startDate=${startDate}&endDate=${endDate}`
    }
    return `/users/report?filter=${filterType}`
  }, [filterType, startDate, endDate])

  const queryClient = useQueryClient();
  // useApiQuery integration
  const {
    data: reportData,
    error,
    isFetching,
    refetch,
  } = useApiQuery(["user-report", reportUrl], reportUrl, {
    enabled: false,
  })

  const handleGenerateReport = () => {
    refetch()
  }

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Job_Report_${new Date().toISOString().split("T")[0]}`,
  })

  const handleDownloadExcel = () => {
    if (!reportData) return

    const workbook = XLSX.utils.book_new()

    // Summary Sheet
    const summaryData = [
      ["Job Performance Report"],
      [""],
      ["Report Period", `${new Date(reportData.summary.dateRange.start).toLocaleDateString()} - ${new Date(reportData.summary.dateRange.end).toLocaleDateString()}`],
      ["Total Jobs", reportData.summary.totalJobs],
      ["New Jobs", reportData.summary.newJobs],
      ["Processing Jobs", reportData.summary.processingJobs],
      ["Completed Jobs", reportData.summary.completedJobs],
      ["Cancelled Jobs", reportData.summary.cancelledJobs],
      ["Total Fees Collected", `₹${reportData.summary.totalFeesCollected.toLocaleString()}`],
      [""],
      ["Status Breakdown"],
      ["Status", "Count"],
    ]

    Object.entries(reportData.summary.byStatus).forEach(([status, count]) => {
      summaryData.push([status.toUpperCase(), count])
    })

    summaryData.push([""])
    summaryData.push(["Sub-Status Breakdown (Processing Jobs)"])
    summaryData.push(["Sub-Status", "Count"])

    Object.entries(reportData.summary.bySubStatus || {}).forEach(([subStatus, count]) => {
      summaryData.push([subStatus, count])
    })

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData)
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary")

    // Jobs Sheet
    const jobsData = [
      [
        "Job ID",
        "Client Name",
        "Job Type",
        "Status",
        "Sub-Status",
        "Fees",
        "Fees Paid",
        "Due Amount",
        "Fee Status",
        "Due Date",
        "Created Date",
        "Assigned To",
      ],
    ]

    reportData.jobs.forEach((job) => {
      jobsData.push([
        job.jobId,
        job.clientName,
        job.jobType,
        job.status,
        job.subStatus || "N/A",
        job.fees,
        job.totalFeesPaid || 0,
        job.dueAmount || 0,
        job.feeStatus,
        new Date(job.dueDate).toLocaleDateString(),
        new Date(job.createdAt).toLocaleDateString(),
        job.assignedTo || "Unassigned",
      ])
    })

    const jobsSheet = XLSX.utils.aoa_to_sheet(jobsData)
    XLSX.utils.book_append_sheet(workbook, jobsSheet, "Jobs")

    // Download
    XLSX.writeFile(workbook, `Job_Report_${new Date().toISOString().split("T")[0]}.xlsx`)
  }

  const handleDownloadPDF = () => {
    handlePrint()
  }

  const getFilterLabel = () => {
    switch (filterType) {
      case "today":
        return "Today"
      case "30days":
        return "Last 30 Days"
      case "month":
        return "This Month"
      case "all":
        return "All Time"
      case "custom":
        return "Custom Range"
      default:
        return "This Month"
    }
  }

  const getStatusColor = (status) => {
    const statusColors = {
      completed: "success",
      processing: "primary",
      new: "info",
      cancelled: "error",
      closed: "default",
    }
    return statusColors[status] || "default"
  }

  const getFeeStatusColor = (status) => {
    const colors = {
      pending: "error",
      // partially_paid: "warning",
      // fully_paid: "success",
      settled: "default",
    }
    return colors[status] || "default"
  }

  const handleClose = () => {
    queryClient.removeQueries(["user-report",reportUrl])
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            Generate Job Performance Report
          </Typography>
          <Button onClick={handleClose} color="inherit">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Filter Selection */}
        {!reportData && (
          <Box sx={{ mb: 3 }}>
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Report Period</InputLabel>
                <Select
                  value={filterType}
                  label="Report Period"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="30days">Last 30 Days</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="custom">Custom Date Range</MenuItem>
                </Select>
              </FormControl>

              {filterType === "custom" && (
                <Stack direction="row" spacing={2}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>
              )}

              {error && (
                <Alert severity="error">
                  {error.message}
                </Alert>
              )}

              <Button
                variant="contained"
                size="large"
                onClick={handleGenerateReport}
                disabled={isFetching || (filterType === "custom" && (!startDate || !endDate))}
                startIcon={isFetching ? <CircularProgress size={20} /> : <CalendarIcon />}
              >
                {isFetching ? "Generating..." : "Generate Report"}
              </Button>
            </Stack>
          </Box>
        )}

        {/* Report Display */}
        {reportData && (
          <Box>
            {/* Action Buttons */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={handleDownloadPDF}>
                Print / Save as PDF
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadExcel}>
                Download Excel
              </Button>
            </Stack>

            {/* Printable Report */}
            <Box ref={reportRef} sx={{ p: 3, bgcolor: "white" }}>
              {/* Report Header */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Job Performance Report
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  {new Date(reportData.summary.dateRange.start).toLocaleDateString()} -{" "}
                  {new Date(reportData.summary.dateRange.end).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Generated on: {new Date().toLocaleString()}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Summary Statistics */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: "info.light" }}>
                      <Typography variant="body2" color="textSecondary">
                        Total Jobs
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {reportData.summary.totalJobs}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: "primary.light" }}>
                      <Typography variant="body2" color="textSecondary">
                        Processing
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {reportData.summary.processingJobs}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: "success.light" }}>
                      <Typography variant="body2" color="textSecondary">
                        Completed
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {reportData.summary.completedJobs}
                      </Typography>
                    </Paper>
                  </Grid>
                  {/* <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: "warning.light" }}>
                      <Typography variant="body2" color="textSecondary">
                        Total Fees Collected
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ₹{reportData.summary.totalFeesCollected.toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid> */}
                </Grid>
              </Box>

              {/* Status Breakdown */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Jobs by Status
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Status</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Count</strong>
                        </TableCell>
                        {/* <TableCell align="right">
                          <strong>Percentage</strong>
                        </TableCell> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(reportData.summary.byStatus).map(([status, count]) => (
                        <TableRow key={status}>
                          <TableCell>
                            <Chip label={status.toUpperCase()} size="small" color={getStatusColor(status)} />
                          </TableCell>
                          <TableCell align="right">{count}</TableCell>
                          {/* <TableCell align="right">
                            {((count / reportData.summary.totalJobs) * 100).toFixed(1)}%
                          </TableCell> */}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Sub-Status Breakdown */}
              {reportData.summary.bySubStatus && Object.keys(reportData.summary.bySubStatus).length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Processing Jobs Breakdown
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Sub-Status</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Count</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(reportData.summary.bySubStatus).map(([subStatus, count]) => (
                          <TableRow key={subStatus}>
                            <TableCell>{subStatus}</TableCell>
                            <TableCell align="right">{count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Detailed Jobs */}
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Detailed Job List
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Job ID</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Client</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Job Type</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Status</strong>
                        </TableCell>
                        {/* <TableCell align="right">
                          <strong>Fees</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Fee Status</strong>
                        </TableCell> */}
                        <TableCell>
                          <strong>Due Date</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.jobs.map((job) => (
                        <TableRow key={job.jobId}>
                          <TableCell sx={{ fontFamily: "monospace" }}>{job.jobId}</TableCell>
                          <TableCell>{job.clientName}</TableCell>
                          <TableCell>{job.jobType}</TableCell>
                          <TableCell>
                            <Chip label={job.status.toUpperCase()} size="small" color={getStatusColor(job.status)} />
                          </TableCell>
                          {/* <TableCell align="right" sx={{ fontWeight: "bold" }}>
                            ₹{job.fees.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={job.feeStatus.replace("_", " ").toUpperCase()}
                              size="small"
                              color={getFeeStatusColor(job.feeStatus)}
                              variant="outlined"
                            />
                          </TableCell> */}
                          <TableCell>{new Date(job.dueDate).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Footer */}
              <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid #ddd", textAlign: "center" }}>
                <Typography variant="caption" color="textSecondary">
                  This is a computer-generated report. For any queries, please contact the administration.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ReportGenerator